/*
 * Reading the base, and turning its records into the shapes the pages read.
 *
 * The plan the workspace is on allows a thousand calls a month and five a
 * second against one base, and the site has nine tables to read. Three things
 * here keep it inside those numbers: no more than four requests are in the air
 * at once, a page is asked for at its largest so one call covers a table, and
 * the first refusal stops the rest of the load rather than spending eight more
 * calls learning the same thing. What keeps the count down across visits is the
 * cache in contentCache.js, which is where most of the saving comes from.
 *
 * The mappers below are also used by scripts/fetchContent.mjs, which can take
 * the same content at build time.
 */
import { AIRTABLE_API_URL, getAirtableConfig } from '../config/airtable';

/* Below Airtable's five a second, leaving room for anything else on the page. */
const MAX_IN_FLIGHT = 4;

/* The largest page Airtable will return, so one call covers a whole table. */
const PAGE_SIZE = 100;

/*
 * The tables, and the field each is read in order of. Site Content is a set of
 * named rows rather than a list, so it sorts by key.
 */
export const CONTENT_TABLES = {
  publications: { name: 'Publications' },
  events: { name: 'Events' },
  teaching: { name: 'Teaching' },
  films: { name: 'Films' },
  editorial: { name: 'Editorial' },
  twentyEightDaysStills: { name: 'Twenty Eight Days' },
  gallery: { name: 'Gallery' },
  navigation: { name: 'Navigation' },
  siteContent: { name: 'Site Content', sortField: 'Key' },
};

/*
 * Airtable answers 429 both when requests arrive too quickly and when the
 * month's allowance is gone. The first clears in a second, the second not until
 * the base's billing month turns over, so the two are worth telling apart
 * before deciding how long to leave Airtable alone.
 */
const describeRefusal = async (response) => {
  let detail = '';
  try {
    const body = await response.json();
    detail = `${body?.error?.type || ''} ${body?.error?.message || ''}`;
  } catch (error) {
    detail = '';
  }

  const monthly = /quota|month/i.test(detail);
  return {
    status: response.status,
    monthlyLimitReached: response.status === 429 && monthly,
    rateLimited: response.status === 429,
    message: detail.trim() || `Airtable answered ${response.status}`,
  };
};

class AirtableError extends Error {
  constructor(tableName, refusal) {
    super(`Could not read "${tableName}" from Airtable: ${refusal.message}`);
    this.name = 'AirtableError';
    this.status = refusal.status;
    this.rateLimited = refusal.rateLimited;
    this.monthlyLimitReached = refusal.monthlyLimitReached;
  }
}

const fetchTable = async (tableName, sortField = 'Sort Order') => {
  const { apiKey, baseId } = getAirtableConfig();
  const records = [];
  let offset = null;

  do {
    const params = new URLSearchParams({ pageSize: String(PAGE_SIZE) });
    if (sortField) {
      params.set('sort[0][field]', sortField);
      params.set('sort[0][direction]', 'asc');
    }
    if (offset) {
      params.set('offset', offset);
    }

    const url = `${AIRTABLE_API_URL}/${baseId}/${encodeURIComponent(tableName)}?${params}`;
    /* A further page can only be asked for once the last one names it. */
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(url, { headers: { Authorization: `Bearer ${apiKey}` } });

    if (!response.ok) {
      // eslint-disable-next-line no-await-in-loop
      throw new AirtableError(tableName, await describeRefusal(response));
    }

    // eslint-disable-next-line no-await-in-loop
    const data = await response.json();
    records.push(...data.records);
    offset = data.offset || null;
  } while (offset);

  return records;
};

export const mapNavItem = (record) => {
  const fields = record.fields || {};

  return {
    id: record.id,
    label: fields.Label || '',
    path: fields.Path || '',
    // An unticked checkbox comes back absent, so an untouched row stays visible.
    hidden: Boolean(fields.Hidden),
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapPublication = (record) => {
  const fields = record.fields || {};
  return {
    id: record.id,
    category: fields.Category || '',
    title: fields.Title || '',
    url: fields.URL || '',
    venue: fields.Venue || '',
    prefix: fields.Prefix || '',
    suffix: fields.Suffix || '',
    plainText: fields['Plain Text'] || '',
    separator: fields.Separator || ' in ',
    plainTitle: Boolean(fields['Plain Title']),
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapEvent = (record) => {
  const fields = record.fields || {};
  return {
    id: record.id,
    title: fields.Title || '',
    url: fields.URL || '',
    date: fields.Date || '',
    plainText: fields['Plain Text'] || '',
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapTeaching = (record) => {
  const fields = record.fields || {};
  return {
    id: record.id,
    description: fields.Description || '',
    sortOrder: fields['Sort Order'] || 0,
  };
};

/*
 * Airtable stores scaled copies alongside every upload. The "large" one is 512px
 * tall, comfortably more than a grid tile needs, while an original straight off a
 * camera can be several thousand pixels wide.
 */
const scaledImageUrl = (attachment) => (
  attachment?.thumbnails?.large?.url || attachment?.url || ''
);

export const mapFilm = (record) => {
  const fields = record.fields || {};
  const imageAttachment = fields.Image?.[0];

  return {
    id: record.id,
    title: fields.Title || '',
    youtubeUrl: fields['YouTube URL'] || '',
    imageUrl: scaledImageUrl(imageAttachment),
    role: fields.Role || '',
    description: fields.Description || '',
    linkUrl: fields['Link URL'] || '',
    linkText: fields['Link Text'] || '',
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapEditorial = (record) => {
  const fields = record.fields || {};

  return {
    id: record.id,
    title: fields.Title || '',
    url: fields.URL || '',
    description: fields.Description || '',
    /*
     * The image now sits inside an opened panel about 470px wide, which the
     * scaled copy covers at nearly twice over, and it is only fetched when a
     * reader opens that panel.
     */
    imageUrl: scaledImageUrl(fields.Image?.[0]),
    /* Blank on rows that predate the field; the page reads those as Elle. */
    section: fields.Section || '',
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapFilmStill = (record) => {
  const fields = record.fields || {};

  return {
    id: record.id,
    name: fields.Name || '',
    imageUrl: scaledImageUrl(fields.Image?.[0]),
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapGalleryImage = (record) => {
  const fields = record.fields || {};
  const attachment = fields.Image?.[0];
  /*
   * This one spans the whole window, so it takes Airtable's "full" copy, capped
   * at 3000px, rather than the 512px one the grids use. The original is left
   * alone because a photo straight off a camera can run to several megabytes.
   */
  const image = attachment?.thumbnails?.full?.url || attachment?.url || '';

  return {
    id: record.id,
    name: fields.Name || '',
    page: fields.Page || '',
    imageUrl: image,
    sortOrder: fields['Sort Order'] || 0,
  };
};

export const mapSiteContent = (records) => {
  const content = {};
  records.forEach((record) => {
    const key = record.fields?.Key;
    const value = record.fields?.Value;
    /*
     * A row left blank reads as one not written yet rather than as an
     * instruction to show nothing, so the copy bundled with the app is left
     * standing. Without this an empty row renders as an empty element: a link
     * with no words in it, say.
     */
    if (key && value?.trim()) {
      content[key] = value;
    }
  });
  return content;
};

/*
 * Site Content rows may carry an attachment instead of (or alongside) text, so
 * images such as the About portrait can be swapped in Airtable. Rows without an
 * attachment are skipped, leaving the component's bundled asset in place.
 */
export const mapSiteImages = (records) => {
  const images = {};
  records.forEach((record) => {
    const key = record.fields?.Key;
    const attachment = record.fields?.Image?.[0];
    if (key && attachment?.url) {
      images[key] = attachment.url;
    }
  });
  return images;
};

/*
 * The whole of the site's content in one object, which is what the snapshot
 * file holds. Bundled defaults are not merged in here: this is meant to be a
 * faithful record of what the base said, and the app decides what to do where
 * it is silent.
 */
export const mapAllContent = (data) => ({
  publications: data.publications.map(mapPublication),
  events: data.events.map(mapEvent),
  teaching: data.teaching.map(mapTeaching),
  films: data.films.map(mapFilm),
  editorial: data.editorial.map(mapEditorial),
  twentyEightDaysStills: data.twentyEightDaysStills.map(mapFilmStill),
  gallery: data.gallery.map(mapGalleryImage),
  navigation: data.navigation.map(mapNavItem),
  siteContent: mapSiteContent(data.siteContent),
  siteImages: mapSiteImages(data.siteContent),
});

const MAPPERS = {
  publications: mapPublication,
  events: mapEvent,
  teaching: mapTeaching,
  films: mapFilm,
  editorial: mapEditorial,
  twentyEightDaysStills: mapFilmStill,
  gallery: mapGalleryImage,
  navigation: mapNavItem,
};

/* Site Content is one table that answers two questions: the words, and the
   pictures attached to the rows that carry them. */
const shapeRecords = (key, records) => {
  if (key === 'siteContent') {
    return { siteContent: mapSiteContent(records), siteImages: mapSiteImages(records) };
  }
  return { [key]: records.map((record) => MAPPERS[key](record)) };
};

/*
 * What the base has to say, as far as it could be read.
 *
 * A table that fails is reported rather than thrown, so one unreadable table
 * costs only that part of the page; the caller keeps what it already had for
 * the rest. A refusal for going too fast or for running out of the month's
 * calls stops the remaining tables, because the next eight requests would only
 * be refused in the same way.
 */
export const fetchContent = async () => {
  const keys = Object.keys(CONTENT_TABLES);
  const queue = [...keys];
  const content = {};
  const read = new Set();
  let refusal = null;

  const worker = async () => {
    while (queue.length > 0 && !refusal) {
      const key = queue.shift();
      const { name, sortField } = CONTENT_TABLES[key];
      try {
        // eslint-disable-next-line no-await-in-loop
        const records = await fetchTable(name, sortField);
        Object.assign(content, shapeRecords(key, records));
        read.add(key);
      } catch (error) {
        if (error.rateLimited) {
          refusal = error;
        }
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(MAX_IN_FLIGHT, keys.length) }, () => worker()),
  );

  const missed = keys.filter((key) => !read.has(key));

  return {
    content,
    missed,
    rateLimited: Boolean(refusal),
    monthlyLimitReached: Boolean(refusal && refusal.monthlyLimitReached),
    error: refusal ? refusal.message : null,
  };
};
